import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  AutoAwesome as SparklesIcon,
  EmojiEvents as CrownIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';
import { SubscriptionPlan } from '../../types/subscription';

interface LimitReachedDialogProps {
  open: boolean;
  feature: string;
  onClose: () => void;
  onUpgrade: (plan?: SubscriptionPlan) => void;
  currentPlan?: SubscriptionPlan;
}

interface PlanCardData {
  plan: SubscriptionPlan;
  name: string;
  price: string;
  icon: React.ReactNode;
  color: string;
  gradient: string;
  highlight: boolean;
}

export const LimitReachedDialog: React.FC<LimitReachedDialogProps> = ({
  open,
  feature,
  onClose,
  onUpgrade,
  currentPlan = 'free'
}) => {
  const { theme } = useTheme();

  const plansToShow: PlanCardData[] = ([
    {
      plan: 'premium' as SubscriptionPlan,
      name: 'Basic',
      price: 'R$ 19,90/mês',
      icon: <SparklesIcon sx={{ fontSize: '1.6rem' }} />,
      color: theme.colors.secondary,
      gradient: 'linear-gradient(135deg, #FFE4E8 0%, #FFF3E0 100%)',
      highlight: currentPlan === 'free',
    },
    {
      plan: 'pro' as SubscriptionPlan,
      name: 'Pro',
      price: 'R$ 25,90/mês',
      icon: <CrownIcon sx={{ fontSize: '1.6rem' }} />,
      color: '#F59E0B',
      gradient: 'linear-gradient(135deg, #FFF3E0 0%, #FFECB3 100%)',
      highlight: currentPlan !== 'free',
    },
  ] as PlanCardData[]).filter(p => p.plan !== currentPlan);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: theme.radii.large,
            backgroundColor: theme.colors.background,
          },
        }
      }}
    >
      <DialogTitle sx={{ position: 'relative', textAlign: 'center', pb: 1 }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.colors.text.secondary,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', px: 3 }}>
        {/* Ícone de bloqueio */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: `${theme.colors.secondary}20`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
          }}
        >
          <BlockIcon sx={{ fontSize: '2.5rem', color: theme.colors.secondary }} />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            color: theme.colors.text.primary,
            mb: 1,
          }}
        >
          Limite Atingido
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: theme.colors.text.secondary,
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          Você atingiu o limite de {feature} do seu plano atual.
          Faça upgrade para continuar criando e organizando seus eventos!
        </Typography>

        {/* Cards dos planos disponíveis */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
          {plansToShow.map(plan => (
            <Box
              key={plan.plan}
              onClick={() => onUpgrade(plan.plan)}
              sx={{
                background: plan.gradient,
                border: `2px solid ${plan.highlight ? plan.color : 'transparent'}`,
                borderRadius: theme.radii.medium,
                p: 2,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'transform 0.15s',
                '&:hover': { transform: 'translateY(-2px)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: plan.color }}>{plan.icon}</Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: theme.colors.text.primary, lineHeight: 1.1 }}>
                      Plano {plan.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                      {plan.highlight ? 'Recomendado para você' : 'Mais recursos e ilimitado'}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.colors.text.primary }}>
                  {plan.price}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth
          sx={{
            borderColor: theme.colors.border,
            color: theme.colors.text.primary,
            borderRadius: theme.radii.medium,
            fontWeight: 'bold',
            '&:hover': {
              borderColor: theme.colors.primary,
              backgroundColor: 'rgba(98, 77, 64, 0.05)',
            },
          }}
        >
          Talvez depois
        </Button>
      </DialogActions>
    </Dialog>
  );
};